import { clientLogger } from '@/lib/clientLogger';
import { useState, useRef, useEffect } from 'react';
import { Edit2, Check, X } from 'lucide-react';
import { useGoogleMapsAutocomplete, getAddressWithZipCode } from '@/hooks/useGoogleMapsAutocomplete';
import { LoadingSpinner } from './LoadingSpinner';

interface EditableAddressProps {
    label?: string;
    value: string;
    onSave: (newValue: string, unitName?: string) => Promise<void>;
    placeholder?: string;
    /** Whether to show the unit name field */
    showUnitField?: boolean;
    /** Current unit name value */
    unitValue?: string;
    /** When true, hides the edit icon and blocks editing */
    disabled?: boolean;
    /** When true, the edit icon is always visible instead of only on hover */
    alwaysShowEdit?: boolean;
}

export function EditableAddress({
    label,
    value,
    onSave,
    placeholder,
    showUnitField = false,
    unitValue = '',
    disabled = false,
    alwaysShowEdit = false,
}: EditableAddressProps) {
    const [isEditing, setIsEditing] = useState(false);
    const [currentValue, setCurrentValue] = useState(value);
    const [currentUnitValue, setCurrentUnitValue] = useState(unitValue);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    // Reset current value when prop value changes
    useEffect(() => {
        if (!isEditing) {
            setCurrentValue(value);
            setCurrentUnitValue(unitValue);
        }
    }, [value, unitValue, isEditing]);

    const handlePlaceSelect = (place: google.maps.places.PlaceResult) => {
        const addressWithZip = getAddressWithZipCode(place);
        if (addressWithZip) {
            setCurrentValue(addressWithZip);
            // Optional: Auto-save on select? Or wait for user to click check?
            // Let's wait for check to be consistent.
        }
    };

    const { error: autocompleteError } = useGoogleMapsAutocomplete({
        inputRef,
        onPlaceSelect: handlePlaceSelect,
        isActive: isEditing,
    });

    const handleSave = async () => {
        // Validation could go here
        setIsSaving(true);
        setError(null);
        try {
            await onSave(currentValue, showUnitField ? currentUnitValue : undefined);
            setIsEditing(false);
        } catch (err) {
            setError('Failed to save');
            clientLogger.error(err);
        } finally {
            setIsSaving(false);
        }
    };

    const handleCancel = () => {
        setIsEditing(false);
        setCurrentValue(value);
        setCurrentUnitValue(unitValue);
        setError(null);
    };

    const formatAddress = (addr: string, unit?: string) => {
        if (!addr) return { line1: 'N/A', line2: '', unit: unit || '' };
        const commaIndex = addr.indexOf(',');
        if (commaIndex === -1) return { line1: addr, line2: '', unit: unit || '' };
        return {
            line1: addr.substring(0, commaIndex).trim(),
            line2: addr.substring(commaIndex + 1).trim(),
            unit: unit || ''
        };
    };

    if (isEditing) {
        return (
            <div className="w-full">
                {label && <label className="text-xs font-medium text-gray-500 mb-1 block">{label}</label>}
                <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-2">
                        <input
                            ref={inputRef}
                            type="text"
                            value={currentValue}
                            onChange={(e) => setCurrentValue(e.target.value)}
                            className={`flex-1 px-2 py-1 text-sm border rounded-md focus:ring-2 focus:stroke-blue-500 focus:border-blue-500 ${error ? 'border-red-500' : 'border-gray-300'}`}
                            placeholder={placeholder}
                            disabled={isSaving}
                            autoComplete="off"
                        />
                        {!showUnitField && (
                            <>
                                <button
                                    onClick={handleSave}
                                    disabled={isSaving}
                                    className="p-1 text-green-600 hover:bg-green-50 rounded transition-colors"
                                    title="Save"
                                >
                                    {isSaving ? <LoadingSpinner /> : <Check className="h-4 w-4" />}
                                </button>
                                <button
                                    onClick={handleCancel}
                                    disabled={isSaving}
                                    className="p-1 text-gray-400 hover:bg-gray-100 rounded transition-colors"
                                    title="Cancel"
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            </>
                        )}
                    </div>
                    {showUnitField && (
                        <div className="flex items-center gap-2">
                            <input
                                type="text"
                                value={currentUnitValue}
                                onChange={(e) => setCurrentUnitValue(e.target.value)}
                                className={`flex-1 px-2 py-1 text-sm border rounded-md focus:ring-2 focus:stroke-blue-500 focus:border-blue-500 ${error ? 'border-red-500' : 'border-gray-300'}`}
                                placeholder="Unit/Business Name # (optional)"
                                disabled={isSaving}
                                autoComplete="off"
                            />
                            <button
                                onClick={handleSave}
                                disabled={isSaving}
                                className="p-1 text-green-600 hover:bg-green-50 rounded transition-colors"
                                title="Save"
                            >
                                {isSaving ? <LoadingSpinner /> : <Check className="h-4 w-4" />}
                            </button>
                            <button
                                onClick={handleCancel}
                                disabled={isSaving}
                                className="p-1 text-gray-400 hover:bg-gray-100 rounded transition-colors"
                                title="Cancel"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        </div>
                    )}
                </div>
                {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
                {autocompleteError && (
                    <p className="text-yellow-600 text-xs mt-1">
                        Address autocomplete unavailable.
                    </p>
                )}
            </div>
        );
    }

    const addr = formatAddress(value, unitValue);

    return (
        <div className="group inline-flex items-start gap-1">
            <div
                className={`flex flex-col leading-snug ${!disabled ? 'cursor-pointer' : ''}`}
                onDoubleClick={() => !disabled && setIsEditing(true)}
            >
                <span className="text-sm text-gray-900">
                    {addr.line1}
                    {showUnitField && addr.unit && <span className="text-gray-600">, {addr.unit}</span>}
                </span>
                {addr.line2 && <span className="text-xs text-secondary-500">{addr.line2}</span>}
            </div>
            {!disabled && (
                <button
                    onClick={() => setIsEditing(true)}
                    className={`p-1 text-gray-400 ${alwaysShowEdit ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'} hover:text-blue-600 hover:bg-blue-50 rounded transition-all flex-shrink-0`}
                    title="Edit"
                >
                    <Edit2 className="h-3 w-3" />
                </button>
            )}
        </div>
    );
}
